import Resource, { ResourceApi } from '../../resource/Resource';
import { USER_RESOURCE, ENROLMENT, COURSE } from '../resourceNames';

import {
  SET_ENROLMENT_DISPLAYED,
  LEAVE_COHORTS,
  COURSE_ENROLMENT_START,
  COURSE_ENROLMENT_FAILURE,
  DISMISS_COURSE_ENROLMENT_ERROR_MODAL,
  COURSE_EXTERNAL_ENROLMENT_START,
  COURSE_EXTERNAL_PAYMENT_START,
  COURSE_PAID_ENROLMENT_START,
  COURSE_ACCESS_CODE_ENROLMENT_START,
  COURSE_ACCESS_CODE_ENROLMENT_CANCEL,
  COURSE_PRE_ENROLMENT_QUESTIONS_ENROLMENT_START,
  COURSE_ENROLMENT_CLASS_SELECTION_START,
  COURSE_ENROLMENT_CLASS_SELECTION_CANCEL,
  CHECK_COURSE_ENROLMENT_ACCESS_CODE_START,
  CHECK_COURSE_ENROLMENT_ACCESS_CODE_SUCCESS,
  CHECK_COURSE_ENROLMENT_ACCESS_CODE_FAILURE,
  COURSE_ENROLMENT_ACCESS_CODE_CHANGE,
  COURSE_ENROLMENT_CLASS_SELECTION_CHANGE,
} from './actions';
import { selectLocationActionObject } from '../location/selectors';
import { currentUserIdSelector, selectCsrfToken } from '../Auth/selectors';
import { getActionForUrl } from '../../web/utilities/reduxLinks';
import { noop } from '../../web/utilities/functions';
import { makeSelectCourseAllowsPrivateClassSelection } from '../CoursePromoData/selectors';
import {
  selectEnrolmentMode,
  selectClassOption,
  selectCourseForEnrolment,
  selectAccessCode,
} from './EnrolmentModal/selectors';
import { ENROLMENT_MODAL_MODE_CLASS_SELECTION } from './EnrolmentModal/constants';
import { ROUTE_COURSE_PAGE, ROUTE_PAYMENT_DETAILS_PAGE } from '../routeActions';
import { cohortCodeFromPath, isCohortPagesFromSubpath } from '../helpers/paths';
import { makeSelectOptimizelyEnabledFeature } from '../Optimizely/selectors';
import { OPTIMIZELY_MULTIPLE_PUBLIC_CLASSES } from '../Optimizely/constants';
import { selectMakePublicCohortsForCourse } from '../Cohorts/selectors';
import { makeMetricsLogEvent } from '../Metrics/manualEventDispatchers';

export * from './dispatchers.common';

// change enrolment setting to show/hide from topbar menu
export const setEnrolmentDisplayed = (enrolmentId, display) => {
  return {
    type: SET_ENROLMENT_DISPLAYED,
    enrolmentId,
    display,
  };
};

// unenrol from multiple cohorts
export const deleteEnrolments = (enrolmentIds) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      ResourceApi.execute('enrolment', 'unenrolFromCohorts', {
        enrolmentIds,
      }).then((response) => {
        const { success } = response;

        dispatch({
          type: LEAVE_COHORTS,
          enrolmentIds: success,
        });

        resolve(response);
      }, reject);
    });
  };
};

export const changeUserCohort = (cohortPath) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const currentUserId = currentUserIdSelector(getState());
      Resource.update(USER_RESOURCE, currentUserId, 'changeCohort', {
        cohortPath,
      }).then(() => {
        // handle cases from the course drop down menu, which puts
        // 'inCohort' in the query param
        const location = selectLocationActionObject(getState());
        const courseSubpath = location.params.subPath;
        let newLocation = {};
        if (location.type === ROUTE_COURSE_PAGE) {
          newLocation = {
            ...location,
            query: {
              ...location.query,
              inCohort: cohortPath,
            },
            state: {
              ...location.state,
              // legacySPA will not trigger a reload so we have to set to false
              legacySPA: false,
            },
          };
          if (isCohortPagesFromSubpath(courseSubpath)) {
            // handle cases where /Groups page and /blog (i.e. Announcement)
            // pages - the url still has /cohorts/ in them and needs to
            // trigger a reload in shouldReload.jsx with the new cohortPath in params
            const newCohortCode = cohortCodeFromPath(cohortPath);
            const segments = courseSubpath.split('/');
            segments[1] = newCohortCode;
            const newCourseSubpath = segments.join('/');
            newLocation = {
              ...location,
              params: {
                ...location.params,
                subPath: newCourseSubpath,
              },
            };
          }
        } else {
          // all other pages that has the cohort switching menu
          // e.g. admin pages, assessment pages
          // should trigger a reload with the correct url
          newLocation = {
            ...location,
            query: {
              ...location.query,
              cohort: cohortPath, // this one is for the url
              inCohort: cohortPath, // this one is for the switching menu display
            },
            state: {
              ...location.state,
              // legacySPA will not trigger a reload so we have to set to false
              legacySPA: false,
            },
          };
        }
        dispatch(newLocation);
        resolve();
      }, reject);
    });
  };
};

const redirectToSubscriptionPaymentUrl = (course, subscriptionPaymentUrl) => {
  const courseId = course.get('id');
  const action = getActionForUrl(subscriptionPaymentUrl);
  return { ...action, query: { courseId } };
};

const doExternalEnrolment = (externalEnrolmentLink) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_EXTERNAL_ENROLMENT_START,
      externalEnrolmentLink,
    });

    window.setTimeout(() => {
      window.location.href = externalEnrolmentLink;
    }, 1000);
  };
};

const doExternalPayment = (externalEnrolmentLink, jwt) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_EXTERNAL_PAYMENT_START,
    });

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = externalEnrolmentLink;

    const jwtInput = document.createElement('input');
    jwtInput.type = 'hidden';
    jwtInput.name = 'jwt';
    jwtInput.value = jwt;
    form.appendChild(jwtInput);

    document.body.appendChild(form);
    form.submit();
  };
};

const doPaidEnrolment = (paymentCreationData) => {
  return (dispatch) => {
    dispatch({ type: COURSE_PAID_ENROLMENT_START });

    // create payment and redirect to payment details page
    ResourceApi.create('payment', paymentCreationData, ['resource'])
      .then((response) => {
        const paymentTransaction = response[0];
        dispatch({
          type: ROUTE_PAYMENT_DETAILS_PAGE,
          params: { paymentTransactionId: paymentTransaction.id },
        });
      })
      .then(noop, (error) => {
        dispatch({
          type: COURSE_ENROLMENT_FAILURE,
          error: {
            title: 'Error creating payment',
            message: error.response.reason,
          },
        });
      });
  };
};

const doAccessCodeEnrolment = {
  type: COURSE_ACCESS_CODE_ENROLMENT_START,
};

export function cancelAccessCodeEnrolment() {
  return (dispatch) => {
    dispatch({ type: COURSE_ACCESS_CODE_ENROLMENT_CANCEL });
  };
}

const doPreEnrolmentQuestionsEnrolment = (course, accessCode, cohortName) => {
  return (dispatch, getState) => {
    dispatch({
      type: COURSE_PRE_ENROLMENT_QUESTIONS_ENROLMENT_START,
      course: course.get('path'),
      accessCode,
      cohortName,
    });

    const csrfToken = selectCsrfToken(getState());
    const form = document.createElement('form');
    form.action = '/courses/enrol/';
    form.method = 'post';

    const formData = [
      { name: 'csrfmiddlewaretoken', value: csrfToken },
      { name: 'course', value: course.get('path') },
      { name: 'enrol', value: 1 },
      { name: 'activationCode', value: accessCode },
      { name: 'cohortName', value: cohortName },
    ];

    formData.forEach(({ name, value }) => {
      const input = document.createElement('input');
      input.value = value;
      input.name = name;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };
};

const createEnrolment = ({
  course,
  accessCode,
  paymentId,
  cohortPath = null,
}) => {
  return (dispatch) => {
    return ResourceApi.create(ENROLMENT, {
      course: course.get('path'),
      cohortPath,
      paymentId,
      accessCode,
    }).then(
      () => {
        if (!paymentId) {
          dispatch(
            makeMetricsLogEvent('GACourse-EnrolledForFree', {
              productID: `COURSE_${course.get('id')}`,
              priceAUD: 0,
            })
          );
        }
        window.location.href = course.get('url');
      },
      () => {
        dispatch({
          type: COURSE_ENROLMENT_FAILURE,
          // enrolment modal has a default error message
        });
      }
    );
  };
};

export const doClassSelectionEnrolment = {
  type: COURSE_ENROLMENT_CLASS_SELECTION_START,
};

export const cancelClassSelectionEnrolment = {
  type: COURSE_ENROLMENT_CLASS_SELECTION_CANCEL,
};

export const updateCourseEnrolmentClassSelectionOption = (classOption) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_ENROLMENT_CLASS_SELECTION_CHANGE,
      classOption,
    });
  };
};

export const updateCourseEnrolmentAccessCode = (accessCode) => {
  return (dispatch) => {
    dispatch({
      type: COURSE_ENROLMENT_ACCESS_CODE_CHANGE,
      accessCode,
    });
  };
};

export const checkAccessCode = (course, accessCode) => {
  return (dispatch) => {
    dispatch({
      type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_START,
      course: course.get('path'),
      accessCode,
    });

    return ResourceApi.read(
      COURSE,
      course.get('path'),
      'getCohortByAccessCode',
      {
        accessCode,
      }
    ).then(
      (result) => {
        if (result) {
          dispatch({
            type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_SUCCESS,
            accessCode,
            cohort: result,
          });
        } else {
          dispatch({
            type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_FAILURE,
            error:
              'The access code is invalid. Please check the access code you have entered and try again.',
          });
        }
      },
      (error) => {
        dispatch({
          type: CHECK_COURSE_ENROLMENT_ACCESS_CODE_FAILURE,
          error: error.response.message,
        });
      }
    );
  };
};

export const enrolInCourse = (
  course,
  accessCode = null,
  onAccessCodeFailureCallback,
  cohortPath = null
) => {
  return (dispatch, getState) => {
    dispatch({
      type: COURSE_ENROLMENT_START,
      course: course.get('path'),
      accessCode,
    });

    const state = getState();
    const mode = selectEnrolmentMode(getState());
    const shouldAllowClassSelection = makeSelectCourseAllowsPrivateClassSelection(
      course.get('path')
    )(state);
    const hasMultiplePublicClasses =
      selectMakePublicCohortsForCourse(state)(course.get('path')).size > 1;

    if (
      (shouldAllowClassSelection || hasMultiplePublicClasses) &&
      mode !== ENROLMENT_MODAL_MODE_CLASS_SELECTION
    ) {
      dispatch(doClassSelectionEnrolment);
      return Promise.resolve();
    }

    return ResourceApi.lookup('course', 'checkEnrolment', {
      course: course.get('path'),
      cohortPath,
      accessCode,
    }).then(
      (result) => {
        const { enrolled, enrolmentMethod } = result;
        if (enrolled) {
          window.location.href = course.get('url');
        } else if (enrolmentMethod === 'subscriptionPayment') {
          const { subscriptionPaymentUrl } = result;
          dispatch(
            redirectToSubscriptionPaymentUrl(course, subscriptionPaymentUrl)
          );
        } else if (enrolmentMethod === 'externalEnrolment') {
          const { externalEnrolmentLink } = result;
          dispatch(doExternalEnrolment(externalEnrolmentLink));
        } else if (enrolmentMethod === 'externalPayment') {
          const { externalEnrolmentLink, jwt } = result;
          dispatch(doExternalPayment(externalEnrolmentLink, jwt));
        } else if (enrolmentMethod === 'paidEnrolment') {
          const { paymentId, paymentCreationData } = result;
          if (paymentId) {
            dispatch(createEnrolment({ course, paymentId, cohortPath }));
          } else {
            dispatch(doPaidEnrolment(paymentCreationData));
          }
        } else if (enrolmentMethod === 'requiresAccessCode') {
          dispatch(doAccessCodeEnrolment);
        } else if (enrolmentMethod === 'requiresPreEnrolmentQuestions') {
          const { cohortName } = result;
          dispatch(
            doPreEnrolmentQuestionsEnrolment(course, accessCode, cohortName)
          );
        } else if (enrolmentMethod === 'requiresPreEnrolmentPermission') {
          const { cohortName } = result;
          // reuse the pre-enrolment question form for explicit email permission
          dispatch(
            doPreEnrolmentQuestionsEnrolment(course, accessCode, cohortName)
          );
        } else {
          dispatch(createEnrolment({ course, accessCode, cohortPath }));
        }
      },
      (error) => {
        if (accessCode) {
          if (onAccessCodeFailureCallback) {
            onAccessCodeFailureCallback(error.response.reason);
          } else {
            dispatch({
              type: COURSE_ENROLMENT_FAILURE,
              error: {
                title: 'Invalid Access Code',
                message: error.response.reason,
              },
            });
          }
        } else if (error.response.error === 'AccessDenied') {
          dispatch({
            type: COURSE_ENROLMENT_FAILURE,
            error: {
              title: 'Access restricted',
              message: error.response.message,
            },
          });
        } else {
          dispatch({
            type: COURSE_ENROLMENT_FAILURE,
          });
        }
      }
    );
  };
};

export const dismissCourseEnrolmentErrorModal = {
  type: DISMISS_COURSE_ENROLMENT_ERROR_MODAL,
};

export const onSubmitClassSelectionModal = () => {
  return (dispatch, getState) => {
    const state = getState();
    const course = selectCourseForEnrolment(state);
    const classOption = selectClassOption(state);
    const isMultiplePublicClassesEnabled = makeSelectOptimizelyEnabledFeature(
      OPTIMIZELY_MULTIPLE_PUBLIC_CLASSES
    )(state);
    const cohortPath = isMultiplePublicClassesEnabled ? classOption : null;

    if (classOption === 'accessCode') {
      const accessCode = selectAccessCode(state);
      return dispatch(enrolInCourse(course, accessCode));
    }

    if (cohortPath) {
      return dispatch(enrolInCourse(course, null, noop, cohortPath));
    }

    // enrol into featured class
    return dispatch(enrolInCourse(course));
  };
};
